import { ReactElement, useEffect, useEffectEvent, useRef } from "react";
import TagManager from "react-gtm-module";
import { useMatch, useSearchParams } from "react-router";
import { useShallow } from "zustand/react/shallow";
import { SpinnerLegacy } from "../../../../../../components";
import { NEW_VENUE } from "../../../../../../constants/routes";
import { useSellarUser } from "../../../../../../hooks";
import { useBusinessJoin } from "../../../../../../hooks/api/v2/business/useBusinessJoin";
import { useSellarCurrentBusiness } from "../../../../../../hooks/useSellarCurrentBusiness.shared";
import {
  SignupFlowStep,
  useSignupContext,
} from "../../../../../../hooks/useSignupContext.shared";
import {
  BuyerCreationFlowStep,
  useBuyerCreationFlowStore,
} from "../BuyerCreationFlowStore";
import { BuyerSignupBusinessClaimedContent } from "./BuyerSignupBusinessClaimedContent";
import { useCreateTwoCompanyCreditLimitQuote } from "../../../../../../hooks/api/v2/two/useCreateTwoCompanyCreditLimitQuote";

export function BuyerSignupBusinessClaimed(): ReactElement | null {
  const creditLimitQuoteMutation = useCreateTwoCompanyCreditLimitQuote();
  const mutateJoinBusiness = useBusinessJoin();
  const { business, loading: businessLoading } = useSellarCurrentBusiness();
  const { changeBusiness } = useSellarUser();
  const {
    setStep: setSignupStep,
    endOnboarding,
    signupDialogActive,
    setRedirectToMarket,
    hasAcceptedTermsAndConditions,
    company,
    setCreditLimitQuoteValue,
    basketTotal,
    setBuyerSignUpBusinessId,
  } = useSignupContext();

  const { setStep, buyerDetails, setBuyerDetails, reset } =
    useBuyerCreationFlowStore(
      useShallow((state) => ({
        setStep: state.setStep,
        buyerDetails: state.buyerDetails,
        setBuyerDetails: state.setBuyerDetails,
        reset: state.reset,
      })),
    );
  const [searchParams] = useSearchParams();

  const storefrontLinkPassword =
    searchParams.get("storefrontLink") ?? searchParams.get("priceTierLink");
  const businessUserInviteJwt = searchParams.get("jwt");
  const utmMedium = searchParams.get("utm_medium");
  const isNewVenue = !!useMatch(NEW_VENUE); // this is to capture buyer creation in the app, rather than in the sign up flow
  const joinedBuyerIdRef = useRef<number | undefined>(undefined);

  const setBusinessOnFrontend = useEffectEvent(async (id: number) => {
    // store created business Id

    setBuyerSignUpBusinessId(id);

    // if creating venue in dashboard, return early and no redirect

    if (isNewVenue) {
      changeBusiness(id);
      return reset();
    }

    // run credit check

    const creditCheckQuote =
      company?.companyNumber && buyerDetails
        ? await creditLimitQuoteMutation.mutateAsync({
            twoOrganisationNumber: company.companyNumber,
            retailerId: buyerDetails.id,
          })
        : null;

    setCreditLimitQuoteValue(creditCheckQuote?.totalLimit ?? 0);

    // used to indicate if user has as draft basket and is signing up via a storefront page

    const hasBasketTotal = !!(basketTotal || basketTotal === 0);

    // if credit check value and no draft basket, navigate to CreditEligibleStep

    if (
      creditCheckQuote?.status === "eligible" &&
      creditCheckQuote.totalLimit > 0 &&
      !hasBasketTotal
    ) {
      return setSignupStep(SignupFlowStep.CreditEligibleStep);
    }

    // if sign up dialog active, show complete onboarding step (handles draft basket)

    if (signupDialogActive) {
      changeBusiness(id);
      return setSignupStep(SignupFlowStep.ConfirmationStep);
    }

    // if buyer creation via sign up flow, end onboarding

    changeBusiness(id);
    setRedirectToMarket(true);
    endOnboarding();
  });

  useEffect(() => {
    if (buyerDetails?.id && joinedBuyerIdRef.current !== buyerDetails.id) {
      joinedBuyerIdRef.current = buyerDetails.id;
      mutateJoinBusiness.mutate(
        {
          data: {
            id: buyerDetails.Business.id,
            hasAcceptedTermsAndConditions,
          },
          sellarMetadata: {
            storefrontLinkPassword,
            businessUserInviteJwt,
            utmMedium,
          },
        },
        {
          onSuccess: async (res) => {
            TagManager.dataLayer({
              dataLayer: {
                event: "venue_joined",
                retailerName: buyerDetails.Business.name,
              },
            });

            if (res.business?.id) {
              return setBusinessOnFrontend(
                parseInt(buyerDetails.Business.id.toString()),
              );
            }
          },
        },
      );
    }
  }, [
    businessUserInviteJwt,
    hasAcceptedTermsAndConditions,
    mutateJoinBusiness,
    buyerDetails,
    storefrontLinkPassword,
    utmMedium,
  ]);

  if (!buyerDetails) {
    setStep(BuyerCreationFlowStep.BuyerBusinessMatchStep);
    return null;
  }

  const hasJoinedNewBusiness = business && !isNewVenue;
  if (
    mutateJoinBusiness.isIdle ||
    mutateJoinBusiness.isPending ||
    businessLoading ||
    hasJoinedNewBusiness
  )
    return <SpinnerLegacy />;

  return (
    <BuyerSignupBusinessClaimedContent
      setBuyerDetails={setBuyerDetails}
      buyer={buyerDetails}
    />
  );
}
